import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ADMIN_TOKEN_KEY, fileUrl } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/statesData";
import { lookupCityCoords } from "@/lib/cityCoords";
import StateManager from "@/components/StateManager";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Upload, X, Eye, EyeOff, MapPin } from "lucide-react";

const EMPTY = {
  name: "", state: "", city: "", location: "",
  rooms: 0, banquet_halls: 0, restaurants: 0,
  description: "", phone: "", image_url: "", external_url: "",
  status: "operational", disabled: false,
  latitude: "", longitude: "",
};

export default function Admin() {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | operational | upcoming | disabled

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listHotels({ limit: 2000 });
      setHotels(data);
    } catch (e) {
      toast.error("Failed to load hotels");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    toast.success("Signed out");
    navigate("/");
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({
      ...EMPTY,
      ...h,
      latitude: h.latitude ?? "",
      longitude: h.longitude ?? "",
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      setForm((f) => ({ ...f, image_url: res.path }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const autoFillCoords = () => {
    if (!form.city) {
      toast.error("Enter city first");
      return;
    }
    const coords = lookupCityCoords(form.city);
    if (coords) {
      setForm((f) => ({ ...f, latitude: coords[0], longitude: coords[1] }));
      toast.success(`Coords set from ${form.city} lookup`);
    } else {
      toast.error(`No coordinates found for "${form.city}" — enter manually`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.state || !form.city || !form.location) {
      toast.error("Please fill required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rooms: Number(form.rooms) || 0,
        banquet_halls: Number(form.banquet_halls) || 0,
        restaurants: Number(form.restaurants) || 0,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      };
      if (editing) {
        await api.updateHotel(editing.id, payload);
        toast.success("Hotel updated");
      } else {
        await api.createHotel(payload);
        toast.success("Hotel added");
      }
      closeForm();
      load();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired"); logout(); }
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteHotel(h.id);
      toast.success("Hotel deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleDisabled = async (h) => {
    try {
      await api.updateHotel(h.id, { disabled: !h.disabled });
      toast.success(h.disabled ? "Hotel enabled" : "Hotel disabled");
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleUpcoming = async (h) => {
    const next = h.status === "upcoming" ? "operational" : "upcoming";
    try {
      await api.updateHotel(h.id, { status: next });
      toast.success(next === "upcoming" ? "Marked upcoming" : "Marked operational");
      load();
    } catch { toast.error("Update failed"); }
  };

  const filtered = hotels.filter((h) => {
    if (filter === "operational" && !(h.status !== "upcoming" && !h.disabled)) return false;
    if (filter === "upcoming" && h.status !== "upcoming") return false;
    if (filter === "disabled" && !h.disabled) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [h.name, h.city, h.state, h.location].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-label text-xs text-[var(--brand-blue)] mb-3">Admin Console</div>
          <h1 className="font-display text-4xl sm:text-6xl leading-none">Hotels.</h1>
          <p className="mt-3 text-sm text-[var(--ink-soft)]">
            {hotels.length} {hotels.length === 1 ? "hotel" : "hotels"} in the deck ({hotels.filter((h) => h.disabled).length} disabled).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="admin-add-hotel-btn"
            onClick={openAdd}
            className="inline-flex items-center gap-2 bg-[var(--brand-blue)] text-white px-4 sm:px-5 py-3 font-display text-xs sm:text-sm hover:bg-black transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Hotel
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 border-2 border-[var(--ink)] px-4 sm:px-5 py-3 font-display text-xs sm:text-sm hover:bg-[var(--ink)] hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
        {[
          ["all", "All"],
          ["operational", "Operational"],
          ["upcoming", "Upcoming"],
          ["disabled", "Disabled"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-filter-${key}`}
            onClick={() => setFilter(key)}
            className={`whitespace-nowrap px-3 py-1.5 font-label text-[11px] border ${
              filter === key
                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--ink)]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mb-6">
        <input
          data-testid="admin-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, city, state, location…"
          className="w-full border-b-2 border-[var(--line)] focus:border-[var(--brand-blue)] outline-none bg-transparent py-3 px-0 text-sm"
        />
      </div>

      {loading ? (
        <div className="text-[var(--ink-soft)] font-label text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--line)] p-10 text-center">
          <h3 className="font-display text-2xl sm:text-3xl mb-2">
            {hotels.length === 0 ? "No hotels yet." : "No matches."}
          </h3>
          <p className="text-sm text-[var(--ink-soft)] mb-5">
            {hotels.length === 0 ? "Click Add Hotel to add your first property." : "Try changing the filter or search term."}
          </p>
          {hotels.length === 0 && (
            <button
              data-testid="admin-add-first-hotel"
              onClick={openAdd}
              className="inline-flex items-center gap-2 bg-[var(--brand-blue)] text-white px-5 py-3 font-display text-sm hover:bg-black"
            >
              <Plus className="w-4 h-4" /> Add First Hotel
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--line)]">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-[var(--paper-2)] border-b border-[var(--line)]">
              <tr className="font-label text-[10px] text-[var(--ink-soft)]">
                <Th>Hotel</Th>
                <Th>Location</Th>
                <Th className="text-right">Rooms</Th>
                <Th className="text-right">Banquets</Th>
                <Th className="text-right">F&B</Th>
                <Th className="text-right">Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr
                  key={h.id}
                  data-testid={`admin-row-${h.id}`}
                  className={`border-b border-[var(--line)] hover:bg-[var(--paper-2)] ${h.disabled ? "opacity-60" : ""}`}
                >
                  <td className="p-3 sm:p-4">
                    <div className="flex items-center gap-3">
                      {h.image_url && (
                        <img
                          src={h.image_url.startsWith("http") ? h.image_url : fileUrl(h.image_url)}
                          alt=""
                          className="w-10 h-10 sm:w-12 sm:h-12 object-cover border border-[var(--line)]"
                        />
                      )}
                      <div>
                        <div className="font-display text-sm sm:text-base">{h.name}</div>
                        <div className="text-xs text-[var(--ink-soft)]">{h.city}, {h.state}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 sm:p-4 text-[var(--ink-soft)] max-w-[240px] truncate">{h.location}</td>
                  <td className="p-3 sm:p-4 text-right font-display text-base">{h.rooms}</td>
                  <td className="p-3 sm:p-4 text-right font-display text-base">{h.banquet_halls}</td>
                  <td className="p-3 sm:p-4 text-right font-display text-base">{h.restaurants}</td>
                  <td className="p-3 sm:p-4 text-right">
                    <div className="inline-flex flex-col items-end gap-1">
                      <span
                        className={`inline-block px-2 py-0.5 font-label text-[9px] ${
                          h.status === "upcoming"
                            ? "bg-[var(--brand-orange)] text-white"
                            : "bg-[var(--paper-3)] text-[var(--ink)]"
                        }`}
                      >
                        {h.status === "upcoming" ? "Upcoming" : "Operational"}
                      </span>
                      {h.disabled && (
                        <span className="inline-block px-2 py-0.5 font-label text-[9px] bg-red-100 text-red-700">
                          Disabled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 sm:p-4">
                    <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
                      <button
                        data-testid={`admin-toggle-upcoming-${h.id}`}
                        onClick={() => toggleUpcoming(h)}
                        title={h.status === "upcoming" ? "Mark operational" : "Mark upcoming"}
                        className="p-2 border border-[var(--line)] hover:border-[var(--brand-orange)]"
                      >
                        <span className="font-label text-[9px]">{h.status === "upcoming" ? "OP" : "UP"}</span>
                      </button>
                      <button
                        data-testid={`admin-toggle-disabled-${h.id}`}
                        onClick={() => toggleDisabled(h)}
                        title={h.disabled ? "Enable" : "Disable"}
                        className="p-2 border border-[var(--line)] hover:border-[var(--ink)]"
                      >
                        {h.disabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        data-testid={`admin-edit-${h.id}`}
                        onClick={() => openEdit(h)}
                        className="p-2 border border-[var(--line)] hover:border-[var(--ink)]"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`admin-delete-${h.id}`}
                        onClick={() => remove(h)}
                        className="p-2 border border-[var(--line)] hover:border-red-500 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <HotelFormModal
          form={form}
          setForm={setForm}
          editing={editing}
          onClose={closeForm}
          onSubmit={submit}
          onUpload={onUpload}
          uploading={uploading}
          saving={saving}
          autoFillCoords={autoFillCoords}
        />
      )}

      {/* State manager section */}
      <div className="mt-12 sm:mt-16">
        <StateManager hotels={hotels} />
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left p-3 sm:p-4 font-normal ${className}`}>{children}</th>;
}

function HotelFormModal({ form, setForm, editing, onClose, onSubmit, onUpload, uploading, saving, autoFillCoords }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 sm:p-8 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl border border-[var(--ink)] my-4 sm:my-8">
        <div className="flex items-center justify-between border-b border-[var(--line)] p-4 sm:p-6 sticky top-0 bg-white z-10">
          <div>
            <div className="font-label text-[10px] text-[var(--brand-blue)] mb-1">
              {editing ? "Edit Hotel" : "New Hotel"}
            </div>
            <h2 className="font-display text-xl sm:text-3xl truncate">{editing ? editing.name : "Add a Hotel"}</h2>
          </div>
          <button
            data-testid="admin-form-close"
            onClick={onClose}
            className="p-2 hover:bg-[var(--paper-2)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <Field label="Hotel Name *" testId="form-name">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="State *" testId="form-state">
              <select value={form.state} onChange={(e) => set("state", e.target.value)} required className={inputCls}>
                <option value="">— Select —</option>
                {INDIAN_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </Field>
            <Field label="City *" testId="form-city">
              <input value={form.city} onChange={(e) => set("city", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Location / Address *" testId="form-location">
              <input value={form.location} onChange={(e) => set("location", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Number of Rooms" testId="form-rooms">
              <input type="number" min="0" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Banquet Halls" testId="form-banquets">
              <input type="number" min="0" value={form.banquet_halls} onChange={(e) => set("banquet_halls", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Restaurants" testId="form-restaurants">
              <input type="number" min="0" value={form.restaurants} onChange={(e) => set("restaurants", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Phone" testId="form-phone">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
            </Field>

            {/* Status + disabled toggles */}
            <Field label="Status" testId="form-status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                <option value="operational">Operational</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </Field>
            <Field label="Visibility" testId="form-disabled">
              <label className="flex items-center gap-3 py-2">
                <input
                  type="checkbox"
                  checked={!form.disabled}
                  onChange={(e) => set("disabled", !e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">
                  {form.disabled ? "Disabled — hidden from public views" : "Enabled — visible to viewers"}
                </span>
              </label>
            </Field>

            {/* Coordinates */}
            <Field label="Latitude" testId="form-latitude">
              <input
                type="number"
                step="any"
                placeholder="e.g. 12.9716"
                value={form.latitude}
                onChange={(e) => set("latitude", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Longitude" testId="form-longitude">
              <input
                type="number"
                step="any"
                placeholder="e.g. 77.5946"
                value={form.longitude}
                onChange={(e) => set("longitude", e.target.value)}
                className={inputCls}
              />
            </Field>
            <div className="sm:col-span-2 -mt-2">
              <button
                type="button"
                data-testid="form-autofill-coords"
                onClick={autoFillCoords}
                className="inline-flex items-center gap-2 border-2 border-[var(--line)] px-4 py-2 font-label text-[11px] hover:border-[var(--ink)]"
              >
                <MapPin className="w-3 h-3" /> Auto-fill from city name
              </button>
              <span className="ml-3 text-[10px] text-[var(--ink-soft)]">
                Uses a bundled lookup of major Indian cities. Overwrites current lat/lng.
              </span>
            </div>

            <Field label="External Details URL" testId="form-external-url" full>
              <input
                type="url" placeholder="https://…" value={form.external_url}
                onChange={(e) => set("external_url", e.target.value)} className={inputCls}
              />
            </Field>
            <Field label="Description" testId="form-description" full>
              <textarea
                rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
                className={`${inputCls} resize-none`}
              />
            </Field>
            <Field label="Image" testId="form-image" full>
              <div className="flex items-center gap-4 flex-wrap">
                {form.image_url && (
                  <img
                    src={form.image_url.startsWith("http") ? form.image_url : fileUrl(form.image_url)}
                    alt="" className="w-24 h-24 object-cover border border-[var(--line)]"
                  />
                )}
                <label
                  data-testid="form-image-upload"
                  className="inline-flex items-center gap-2 border-2 border-dashed border-[var(--line)] px-4 py-3 font-label text-[11px] cursor-pointer hover:border-[var(--ink)]"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : form.image_url ? "Replace Image" : "Upload Image"}
                  <input type="file" accept="image/*" hidden onChange={onUpload} />
                </label>
                {form.image_url && (
                  <button
                    type="button" onClick={() => set("image_url", "")}
                    className="text-xs text-[var(--ink-soft)] hover:text-red-500 underline"
                  >
                    Remove
                  </button>
                )}
              </div>
            </Field>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[var(--line)]">
            <button
              type="button" onClick={onClose}
              className="px-4 sm:px-5 py-3 border-2 border-[var(--ink)] font-display text-xs sm:text-sm hover:bg-[var(--paper-2)]"
            >
              Cancel
            </button>
            <button
              data-testid="form-submit" type="submit" disabled={saving}
              className="px-4 sm:px-5 py-3 bg-[var(--brand-blue)] text-white font-display text-xs sm:text-sm hover:bg-black disabled:opacity-50"
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Hotel"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full border-b-2 border-[var(--line)] focus:border-[var(--brand-blue)] outline-none bg-transparent py-2 px-0 text-sm";

function Field({ label, children, full, testId }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testId}>
      <label className="font-label text-[10px] text-[var(--ink-soft)] block mb-2">{label}</label>
      {children}
    </div>
  );
}
